import React, { Dispatch, SetStateAction, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ProviderOrder } from '../../models/provider.order.model';
import { ProviderInvoice } from '../../models/provider.invoice.model';
import { Expense } from '../../models/expense.model';
import { MediaDA } from '../../services/admin/media.service';
import { GlobalSwal } from '../../styles/swal.style';
import { modalHide } from '../../utils/modal.util';
import { uploadTooltip } from '../../utils/tooltip.util';
import { uploadPdf } from '../../scripts/file.load.script';
import { navBarShow } from '../../utils/nav.bar.util';

export interface AppModalVoucherUploadFileProps {
  setAsideMinimize: Dispatch<SetStateAction<string>>,
  record: any | ProviderOrder | ProviderInvoice | Expense,
  idCompany: string,
  vouchers: {list: boolean, value: {name: string, size: number, type: string, view: string, file: any, id_document_inbox?: string}[], valid: {name: boolean, file: boolean}[]},
  setVouchers: Dispatch<SetStateAction<{list: boolean, value: {name: string, size: number, type: string, view: string, file: any, id_document_inbox?: string}[], valid: {name: boolean, file: boolean}[]}>>
};

const AppModalVoucherUploadFile: React.FunctionComponent<AppModalVoucherUploadFileProps> = ({setAsideMinimize, record, idCompany, vouchers, setVouchers}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [uploadVouchersValue, setUploadVouchersValue] = useState<{name: string, size: number, type: string, view: string, file: any}[]>([])
  const [uploadVouchersValid, setUploadVouchersValid] = useState<{file: boolean, message: string}[]>([])

  const handleChangeFile = async (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setLoadIndicator('on')

    let archivesCapture = event.target.files
    let archivesValue: {name: string, size: number, type: string, view: string, file: any}[] = []
    let archivesValid: {file: boolean, message: string}[] = []

    for (let item of archivesCapture) {
      let archive = await uploadPdf(lang, item)
      archivesValue.push(archive.value)
      archivesValid.push(archive.valid)
    }

    setUploadVouchersValue(archivesValue)
    setUploadVouchersValid(archivesValid)
    setLoadIndicator('off')
  }

  const executeUploadFile = async () => {
    let uploadVouchersIsValid: boolean = uploadVouchersValid.every((item) => (item.file === true))

    if (uploadVouchersIsValid) {
      setLoadIndicator('on')

      const uploadedItems: {name: string, size: number, type: string, view: string, file: any, id_document_inbox?: string}[] = []

      for (const item of uploadVouchersValue) {
        if (idCompany && item.file instanceof File) {
          const formData = new FormData()
          formData.append('file', item.file)
          const response = await MediaDA.postDocument(idCompany, formData, 'web')
          if (response.status === 201 && response.data) {
            const docId = response.data.id ?? response.data._id
            uploadedItems.push({...item, id_document_inbox: docId})
          } else {
            GlobalSwal.fire({
              title: lang.labels.incorrectFiles,
              text: `${lang.labels.errorOccurredWhileProcessingRequest}: ${item.name}`,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
            setLoadIndicator('off')
            return
          }
        } else {
          uploadedItems.push(item)
        }
      }

      setVouchers({...vouchers, value: vouchers.value.concat(uploadedItems), valid: vouchers.valid.concat(uploadedItems.map(() => ({name: true, file: true})))})
      executeHideModaProviderUploadFile()

      if (record && record.status === 'draft' && uploadedItems.length > 0 && uploadVouchersValid.length > 0) {
        setAsideMinimize("on")
        navBarShow('button-navbar-aside-minimize')
      }

      setLoadIndicator('off')
    } else {
      GlobalSwal.fire({
        title: lang.labels.incorrectFiles,
        text: lang.labels.someOfTheUploadedFilesDoNotMeetTheRequiredCharacteristics,
        icon: 'error',
        confirmButtonText: lang.labels.okGotIt
      })
    }
  }

  const executeRemoveFile = (index: number) => {
    let tempUploadVouchersValue = uploadVouchersValue.filter((_, temp_index) => (temp_index !== index))
    let tempUploadVouchersValid = uploadVouchersValid.filter((_, temp_index) => (temp_index !== index))

    setUploadVouchersValue(tempUploadVouchersValue)
    setUploadVouchersValid(tempUploadVouchersValid)
    restartModal()
  }

  const executeRemoveAllFiles = () => {
    setUploadVouchersValue([])
    setUploadVouchersValid([])
    restartModal()
  }

  const executeHideModaProviderUploadFile = () => {
    modalHide('modal-voucher-upload-file')
    uploadTooltip()

    setTimeout( () => {
      executeRemoveAllFiles()
    }, 200 )
  }

  function restartModal() {
    Array.from(document.querySelectorAll('input[name="voucher"]')).forEach( (node)  => {
      let element = node as HTMLInputElement
      element.value = ''
    })
  }

  return (
    <div id="modal-voucher-upload-file" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.uploadFiles}</h2>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <div className="dropzone dropzone-queue cursor-none">
                <div className="dropzone-panel">
                  <label className="btn btn-sm btn-primary" data-kt-indicator={loadIndicator}>
                    <input className="btn-check w-100" type="file" name="voucher" accept=".pdf" multiple onChange={handleChangeFile} />
                    <span className="indicator-label">{lang.labels.attachFiles}</span>
                    <span className="indicator-progress">
                      {lang.labels.pleaseWait}
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  </label>
                  { uploadVouchersValue.length > 1 &&
                    <button className="btn btn-sm btn-light-primary ms-2" type="button" onClick={executeRemoveAllFiles}>{lang.labels.removeAll}</button>
                  }
                </div>
                <div className="separator my-2"></div>
                { uploadVouchersValue.length > 0 &&
                  <>
                    <div className="dropzone-items">
                      { uploadVouchersValue.map (( (item, index) => { return (
                        <div key={index} className="dropzone-item dz-image-preview my-1">
                          <div className="dropzone-file">
                            <div className="dropzone-filename text-dark">
                              <span className="me-1">{item.name}</span>
                              <strong>
                                (
                                <span>
                                  <strong className="me-1">{item.size}</strong>
                                  <strong>{lang.labels.mb}</strong>
                                </span>
                                )
                              </strong>
                            </div>
                            { !uploadVouchersValid[index].file &&
                              <div className="dropzone-error m-0">{uploadVouchersValid[index].message}</div>
                            }
                          </div>
                          <div className="dropzone-toolbar">
                            <span className="dropzone-delete" onClick={() => executeRemoveFile(index)}>
                              <i className="bi bi-x fs-1"></i>
                            </span>
                          </div>
                        </div>
                      )}))}
                    </div>
                    <div className="separator my-2"></div>
                  </>
                }
              </div>
              <span className="form-text text-justify">{lang.labels.fileFormatMaximum10MBPerFile}</span>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModaProviderUploadFile}>{lang.labels.discard}</button>
            <button className={`${uploadVouchersValue.length > 0 ? "btn btn-primary" : "d-none"} mx-2`} type="button" onClick={executeUploadFile}>{lang.labels.addFiles}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalVoucherUploadFile;
